/**
 * Loadout — the cosmetics a player will wear when they next spawn.
 *
 * Pure state (no DOM, no network, no PIXI): { skin, trail, backpack } as catalog ids.
 * Ids are always checked against config/cosmetics.json; anything unknown
 * resolves to the slot's `isDefault` entry so a skin removed from config can
 * never be "equipped". LoadoutStore seeds and persists it; LockerScreen edits
 * it; client.js sends toJoinPayload() in setPlayerName (spec 6A D4/D6).
 */
export const SLOT_KEYS = Object.freeze({ skin: 'playerSkins', trail: 'trails', backpack: 'backpacks' });

export class Loadout {
  constructor(cosmeticsConfig) {
    this.config = cosmeticsConfig || {};
    this.listeners = new Set();
    this.state = {};
    for (const slot of Object.keys(SLOT_KEYS)) this.state[slot] = this.defaultId(slot);
  }

  entries(slot) {
    const key = SLOT_KEYS[slot];
    return key ? (this.config[key] || []) : [];
  }

  defaultId(slot) {
    const entries = this.entries(slot);
    return (entries.find((e) => e.isDefault) || entries[0] || { id: 'default' }).id;
  }

  isKnown(slot, id) {
    return typeof id === 'string' && this.entries(slot).some((e) => e.id === id);
  }

  get() {
    return { ...this.state };
  }

  /** Equip a known id; returns false (and changes nothing) for unknown ids. */
  equip(slot, id) {
    if (!this.isKnown(slot, id)) return false;
    if (this.state[slot] === id) return true;
    this.state[slot] = id;
    this.notify();
    return true;
  }

  /** Bulk set from storage or the account row; unknown/null ids → slot default. No notify (seeding). */
  set(partial = {}) {
    for (const slot of Object.keys(SLOT_KEYS)) {
      if (!(slot in partial)) continue;
      this.state[slot] = this.isKnown(slot, partial[slot]) ? partial[slot] : this.defaultId(slot);
    }
  }

  toJoinPayload() {
    return { skinId: this.state.skin, trailId: this.state.trail, backpackId: this.state.backpack };
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  notify() {
    const snapshot = this.get();
    for (const fn of this.listeners) fn(snapshot);
  }
}
